import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/authStore";
import { RoleSelector } from "./RoleSelector";
import { TrimmedLogo } from "./TrimmedLogo";
import { SidebarGroup } from "@/components/superadmin/SidebarGroup";
import { onCascadeStateChanged } from "@/utils/cascadeEvents";
import { engineerVisiblePath } from "@/utils/engineerNav";
import { schoolBillingVisible } from "@/utils/schoolBilling";
import { teacherChatApi } from "@/services/api/teacherChat";
import { directMessageApi } from "@/services/api/directMessages";
import { helpApi } from "@/services/api/help";
import { directorApi } from "@/services/api/director";
import { hrApi } from "@/services/api/hr";
import { timetableExportApi } from "@/services/api/timetableExport";
import {
  getPublicSystemBranding,
  poweredByHref,
  SYSTEM_BRANDING_PUBLIC_KEY,
} from "@/services/api/systemBranding";
import {
  LayoutDashboard,
  Users,
  UserCog,
  FileSpreadsheet,
  Wrench,
  Repeat,
  MessageSquareReply,
  ShieldCheck,
  GraduationCap,
  Settings,
  CalendarDays,
  CalendarClock,
  BellRing,
  AlertTriangle,
  FileText,
  Link as LinkIcon,
  Mail,
  Activity,
  Target,
  Gauge,
  UserCheck,
  Clock,
  CircleHelp,
  ArrowRightLeft,
  Inbox,
  Send,
  MessageSquare,
  Archive,
  Sparkles,
  ClipboardList,
  CalendarCheck,
  Palette,
  Monitor,
  BarChart3,
  Coins,
  CircleDollarSign,
  Wallet,
  Percent,
  Mic,
  Eye,
} from "lucide-react";

// Unread badges: poll calmly (60s), never while the tab is hidden; explicit
// invalidations (new message, cascade events) still refresh immediately.
const UNREAD_POLL_MS = 60000;

interface NavItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
  badge?: number;
  disabled?: boolean;
  disabledHint?: string;
  /** HR-v2: Seite nur anzeigen, wenn dieser Seiten-Scope aktiv ist */
  scopePage?: "finance_queue" | "ipb_payouts" | "statistics";
}

interface NavGroup {
  title: string;
  icon: React.ReactNode;
  groupKey: string;
  items: NavItem[];
}

const ROLE_NAV_FLAT: Record<string, NavItem[]> = {
  director: [
    {
      label: "Dashboard",
      path: "/director/dashboard",
      icon: <LayoutDashboard className="w-4 h-4" />,
    },
    {
      label: "Nachrichten",
      path: "/messages",
      icon: <Mail className="w-4 h-4" />,
    },
    {
      label: "Alle Fälle",
      path: "/director/decisions",
      icon: <UserCheck className="w-4 h-4" />,
    },
    {
      label: "Stundenplan",
      path: "/director/stundenplan",
      icon: <CalendarDays className="w-4 h-4" />,
    },
    {
      label: "Stellvertretungen",
      path: "/director/delegations",
      icon: <ArrowRightLeft className="w-4 h-4" />,
    },
    {
      label: "Superadmin- / Direktor-Verwaltung",
      path: "/director/users/superadmins",
      icon: <ShieldCheck className="w-4 h-4" />,
    },
    // Direktorkonfig v2 (D11): eigener Einstellungen-Bereich
    {
      label: "Plan-Export",
      path: "/timetable-export",
      icon: <CalendarDays className="w-4 h-4" />,
    },
    {
      label: "Einstellungen",
      path: "/director/settings",
      icon: <Settings className="w-4 h-4" />,
    },
    { label: "Hilfe", path: "/help", icon: <CircleHelp className="w-4 h-4" /> },
  ],
  teacher: [
    {
      label: "Dashboard",
      path: "/teacher/dashboard",
      icon: <LayoutDashboard className="w-4 h-4" />,
    },
    {
      label: "Neue Anfrage",
      path: "/teacher/new-request",
      icon: <FileSpreadsheet className="w-4 h-4" />,
    },
    {
      label: "Stundenplan",
      path: "/teacher/timetable",
      icon: <CalendarDays className="w-4 h-4" />,
    },
    {
      label: "Anfragen an mich",
      path: "/teacher/incoming-requests",
      icon: <Inbox className="w-4 h-4" />,
    },
    {
      label: "Anfragen von mir",
      path: "/teacher/requests",
      icon: <Send className="w-4 h-4" />,
    },
    {
      label: "Meine Vertretungen",
      path: "/teacher/bookings",
      icon: <CalendarCheck className="w-4 h-4" />,
    },
    {
      label: "Chats",
      path: "/teacher/chats",
      icon: <MessageSquare className="w-4 h-4" />,
    },
    {
      label: "Nachrichten",
      path: "/messages",
      icon: <Mail className="w-4 h-4" />,
    },
    {
      label: "Einstellungen",
      path: "/teacher/settings",
      icon: <Settings className="w-4 h-4" />,
    },
    { label: "Hilfe", path: "/help", icon: <CircleHelp className="w-4 h-4" /> },
  ],
  engineer: [
    {
      label: "Nachrichten",
      path: "/messages",
      icon: <Mail className="w-4 h-4" />,
    },
    { label: "Hilfe", path: "/help", icon: <CircleHelp className="w-4 h-4" /> },
  ],
  partner: [
    {
      label: "Portfolio",
      path: "/partner/schools",
      icon: <Users className="w-4 h-4" />,
    },
    {
      label: "Superadmins",
      path: "/partner/superadmins",
      icon: <UserCog className="w-4 h-4" />,
    },
    {
      label: "Team",
      path: "/partner/team",
      icon: <ShieldCheck className="w-4 h-4" />,
    },
    {
      label: "Abrechnung",
      path: "/partner/billing",
      icon: <CircleDollarSign className="w-4 h-4" />,
    },
    { label: "Hilfe", path: "/help", icon: <CircleHelp className="w-4 h-4" /> },
  ],
};

const ROLE_NAV_GROUPS: Record<string, NavGroup[]> = {
  ultraadmin: [
    {
      title: "Dashboard",
      icon: <LayoutDashboard className="w-4 h-4" />,
      groupKey: "dashboard",
      items: [{ label: "Dashboard", path: "/ultraadmin/dashboard" }],
    },
    {
      title: "Wirtschaft",
      icon: <CircleDollarSign className="w-4 h-4" />,
      groupKey: "wirtschaft",
      items: [
        {
          label: "Listenpreise",
          path: "/ultraadmin/prices",
          icon: <CircleDollarSign className="w-4 h-4" />,
        },
        {
          label: "Schul-Konditionen",
          path: "/ultraadmin/school-terms",
          icon: <Percent className="w-4 h-4" />,
        },
      ],
    },
    {
      title: "Schulen & User",
      icon: <Users className="w-4 h-4" />,
      groupKey: "schools",
      items: [
        {
          label: "Schulen",
          path: "/ultraadmin/schools",
          icon: <Users className="w-4 h-4" />,
        },
        {
          label: "Brille aufsetzen",
          path: "/ultraadmin/act-as",
          icon: <UserCog className="w-4 h-4" />,
        },
        {
          label: "Partner-Firmen",
          path: "/ultraadmin/partners",
          icon: <UserCog className="w-4 h-4" />,
        },
        {
          label: "Superuser",
          path: "/ultraadmin/superusers",
          icon: <UserCog className="w-4 h-4" />,
        },
        {
          label: "Statistik",
          path: "/ultraadmin/statistics",
          icon: <FileSpreadsheet className="w-4 h-4" />,
        },
      ],
    },
    {
      title: "Einstellungen",
      icon: <Settings className="w-4 h-4" />,
      groupKey: "settings",
      items: [
        { label: "Einstellungen", path: "/ultraadmin/settings" },
        {
          label: "Datenqualität",
          path: "/superadmin/timetable-data-quality",
          icon: <ClipboardList className="w-4 h-4" />,
        },
        {
          label: "Rechnungen",
          path: "/ultraadmin/invoices",
          icon: <CircleDollarSign className="w-4 h-4" />,
        },
        {
          label: "Billing-Tickets",
          path: "/ultraadmin/billing-tickets",
          icon: <Mail className="w-4 h-4" />,
        },
      ],
    },
    {
      title: "Hilfe",
      icon: <CircleHelp className="w-4 h-4" />,
      groupKey: "help",
      items: [{ label: "Hilfe", path: "/help" }],
    },
  ],
  superadmin: [
    {
      title: "Dashboard",
      icon: <LayoutDashboard className="w-4 h-4" />,
      groupKey: "dashboard",
      items: [{ label: "Dashboard", path: "/superadmin/dashboard" }],
    },
    {
      title: "Benutzer & Import",
      icon: <Users className="w-4 h-4" />,
      groupKey: "benutzer",
      items: [
        {
          label: "Benutzer & Import",
          path: "/superadmin/users",
          icon: <UserCog className="w-4 h-4" />,
        },
        {
          label: "Stundenplan-Import",
          path: "/superadmin/timetable-import",
          icon: <FileSpreadsheet className="w-4 h-4" />,
        },
        {
          label: "Datenqualität",
          path: "/superadmin/timetable-data-quality",
          icon: <ClipboardList className="w-4 h-4" />,
        },
        {
          label: "Engineers",
          path: "/superadmin/engineers",
          icon: <Wrench className="w-4 h-4" />,
        },
      ],
    },
    {
      title: "Qualifikation",
      icon: <GraduationCap className="w-4 h-4" />,
      groupKey: "qualifikation",
      items: [
        {
          label: "Qualifikations-Matrix",
          path: "/admin/qualifications",
          icon: <GraduationCap className="w-4 h-4" />,
        },
        {
          label: "Lehrer-Parameter",
          path: "/superadmin/settings/teacher-params",
          icon: <Settings className="w-4 h-4" />,
        },
      ],
    },
    {
      title: "Einstellungen",
      icon: <Settings className="w-4 h-4" />,
      groupKey: "einstellungen",
      items: [
        {
          label: "Zahlung",
          path: "/zahlung",
          icon: <CircleDollarSign className="w-4 h-4" />,
        },
        {
          label: "Vertretungsanfragen",
          path: "/superadmin/settings/substitution_request",
          icon: <ArrowRightLeft className="w-4 h-4" />,
        },
        {
          label: "Zeiteinstellungen",
          path: "/superadmin/settings/timeout",
          icon: <Clock className="w-4 h-4" />,
        },
        {
          label: "Kaskade",
          path: "/superadmin/settings/cascade",
          icon: <Sparkles className="w-4 h-4" />,
        },
        {
          label: "Panikzone",
          path: "/superadmin/settings/panic_zone",
          icon: <AlertTriangle className="w-4 h-4" />,
        },
        {
          label: "Response",
          path: "/superadmin/settings/response",
          icon: <MessageSquareReply className="w-4 h-4" />,
        },
        {
          label: "Live-Status",
          path: "/superadmin/settings/live_status",
          icon: <Activity className="w-4 h-4" />,
        },
        {
          label: "Radar",
          path: "/superadmin/settings/radar",
          icon: <Target className="w-4 h-4" />,
        },
        {
          label: "Konfidenz",
          path: "/superadmin/settings/confidence",
          icon: <Gauge className="w-4 h-4" />,
        },
        {
          label: "Stundenplan",
          path: "/superadmin/settings/timetable",
          icon: <CalendarDays className="w-4 h-4" />,
        },
        {
          label: "Plan-Export",
          path: "/superadmin/settings/timetable_export",
          icon: <CalendarDays className="w-4 h-4" />,
        },
        {
          label: "Auto Re-Request",
          path: "/superadmin/settings/auto_re_request",
          icon: <Repeat className="w-4 h-4" />,
        },
        {
          label: "Kalender",
          path: "/superadmin/settings/calendar",
          icon: <CalendarDays className="w-4 h-4" />,
        },
        {
          label: "Direktor",
          path: "/superadmin/settings/director",
          icon: <UserCheck className="w-4 h-4" />,
        },
        // hr2-v2 (E10): HR-Modul in den Einstellungen integriert (E1) —
        // Finanz-Icon statt doppeltem GraduationCap (Qualifikation)
        {
          label: "HR-Modul",
          path: "/superadmin/settings/hr",
          icon: <CircleDollarSign className="w-4 h-4" />,
        },
        {
          label: "E-Mail",
          path: "/superadmin/email-templates",
          icon: <Mail className="w-4 h-4" />,
        },
        {
          label: "Reminder-Konfiguration",
          path: "/superadmin/settings/reminder",
          icon: <BellRing className="w-4 h-4" />,
        },
        {
          label: "Integrationen",
          path: "/superadmin/settings/integration",
          icon: <LinkIcon className="w-4 h-4" />,
        },
        {
          label: "Schul-Design",
          path: "/superadmin/settings/design",
          icon: <Palette className="w-4 h-4" />,
        },
        {
          label: "Anzeigetafel",
          path: "/superadmin/settings/display_board",
          icon: <Monitor className="w-4 h-4" />,
        },
        {
          label: "Extraktion Regex",
          path: "/superadmin/patterns",
          icon: <Target className="w-4 h-4" />,
        },
        {
          label: "Rules of Engagement",
          path: "/superadmin/settings/rules_of_engagement",
          icon: <ShieldCheck className="w-4 h-4" />,
        },
        {
          label: "Dateien",
          path: "/superadmin/files",
          icon: <FileText className="w-4 h-4" />,
        },
        {
          label: "Admin-Bereich",
          path: "/superadmin/settings/admin_dashboard",
          icon: <LayoutDashboard className="w-4 h-4" />,
        },
        {
          label: "Aufbewahrung",
          path: "/superadmin/settings/dm",
          icon: <Archive className="w-4 h-4" />,
        },
        {
          label: "Talk",
          path: "/superadmin/settings/talk",
          icon: <Mic className="w-4 h-4" />,
        },
      ],
    },
    {
      title: "Postausgang",
      icon: <Inbox className="w-4 h-4" />,
      groupKey: "postausgang",
      items: [
        {
          label: "Postausgang",
          path: "/superadmin/email-outbox",
          icon: <Inbox className="w-4 h-4" />,
        },
      ],
    },
    {
      title: "Audit & Hilfe",
      icon: <Clock className="w-4 h-4" />,
      groupKey: "audit",
      items: [
        {
          label: "Audit-Log",
          path: "/superadmin/audit-log",
          icon: <Clock className="w-4 h-4" />,
        },
        {
          label: "Hilfe",
          path: "/help",
          icon: <CircleHelp className="w-4 h-4" />,
        },
      ],
    },
  ],
  admin: [
    {
      title: "Dashboard",
      icon: <LayoutDashboard className="w-4 h-4" />,
      groupKey: "dashboard",
      items: [
        { label: "Vertretungen", path: "/admin/substitutions" },
        {
          label: "Auto Re-Request",
          path: "/admin/auto-re-request",
          icon: <Repeat className="w-4 h-4" />,
        },
        { label: "Wartepool", path: "/admin/waiting-pool" },
      ],
    },
    {
      title: "Anfrage für Lehrer",
      icon: <Send className="w-4 h-4" />,
      groupKey: "anfrage-fuer-lehrer",
      items: [
        { label: "Anfrage für Lehrer", path: "/admin/anfrage-fuer-lehrer" },
      ],
    },
    {
      title: "Admin-Stellvertretung",
      icon: <CalendarCheck className="w-4 h-4" />,
      groupKey: "admin-stellvertretung",
      items: [
        { label: "Admin-Stellvertretung", path: "/admin/stellvertretung" },
      ],
    },
    {
      title: "Sneak-Peek",
      icon: <Eye className="w-4 h-4" />,
      groupKey: "sneak-peek",
      items: [{ label: "Sneak-Peek", path: "/admin/sneak-peek" }],
    },
    {
      title: "Vertretung",
      icon: <CalendarClock className="w-4 h-4" />,
      groupKey: "vertretung",
      items: [
        { label: "Stellvertretungsliste", path: "/admin/substitutions-log" },
        {
          label: "Stornierungen",
          path: "/admin/cancellations",
          icon: <ArrowRightLeft className="w-4 h-4" />,
        },
        { label: "Live-Status", path: "/admin/live-status" },
      ],
    },
    {
      title: "Stundenplan",
      icon: <CalendarDays className="w-4 h-4" />,
      groupKey: "stundenplan",
      items: [
        {
          label: "Import",
          path: "/admin/timetables",
          icon: <FileSpreadsheet className="w-4 h-4" />,
        },
        {
          label: "Drucken",
          path: "/admin/stundenplan",
          icon: <CalendarDays className="w-4 h-4" />,
        },
        {
          label: "Export",
          path: "/timetable-export",
          icon: <CalendarDays className="w-4 h-4" />,
        },
      ],
    },
    {
      title: "Verwaltung",
      icon: <Users className="w-4 h-4" />,
      groupKey: "verwaltung",
      items: [
        {
          label: "Konflikte",
          path: "/admin/conflicts",
          icon: <AlertTriangle className="w-4 h-4" />,
        },
        { label: "Qualifikationen", path: "/admin/qualifications" },
        {
          label: "Lehrer-Parameter",
          path: "/admin/school-params",
          icon: <Settings className="w-4 h-4" />,
        },
        { label: "Admins", path: "/admin/admins" },
        { label: "Lehrer-Validierung", path: "/admin/teachers" },
      ],
    },
    {
      title: "Einstellungen",
      icon: <Settings className="w-4 h-4" />,
      groupKey: "einstellungen",
      items: [
        {
          label: "Admin-Einstellungen",
          path: "/admin/settings",
          icon: <Settings className="w-4 h-4" />,
        },
        {
          label: "Kalender",
          path: "/admin/settings/calendar",
          icon: <CalendarDays className="w-4 h-4" />,
        },
      ],
    },
    {
      title: "Hilfe",
      icon: <CircleHelp className="w-4 h-4" />,
      groupKey: "help",
      items: [{ label: "Hilfe", path: "/help" }],
    },
  ],
  hr: [
    {
      title: "Arbeiten",
      icon: <ClipboardList className="w-4 h-4" />,
      groupKey: "arbeiten",
      items: [
        {
          label: "Plan-Export",
          path: "/timetable-export",
          icon: <CalendarDays className="w-4 h-4" />,
        },
        {
          label: "Lehrerpult",
          path: "/hr/pult",
          icon: <ClipboardList className="w-4 h-4" />,
        },
        {
          label: "Abrechnungstabelle",
          path: "/hr/tabelle",
          icon: <BarChart3 className="w-4 h-4" />,
        },
      ],
    },
    {
      title: "Konto & Berichte",
      icon: <Coins className="w-4 h-4" />,
      groupKey: "konto",
      items: [
        {
          label: "IPB & Auszahlungen",
          path: "/hr/ipb",
          scopePage: "ipb_payouts",
          icon: <Coins className="w-4 h-4" />,
        },
        {
          label: "Statistik & Berichte",
          path: "/hr/reports",
          scopePage: "statistics",
          icon: <Wallet className="w-4 h-4" />,
        },
      ],
    },
    {
      title: "Einstellungen",
      icon: <Settings className="w-4 h-4" />,
      groupKey: "einstellungen",
      items: [{ label: "Einstellungen", path: "/hr/settings" }],
    },
    {
      title: "Hilfe",
      icon: <CircleHelp className="w-4 h-4" />,
      groupKey: "help",
      items: [
        {
          label: "Hilfe",
          path: "/help",
          icon: <CircleHelp className="w-4 h-4" />,
        },
      ],
    },
  ],
};

function buildEngineerGroups(scopes: string[]): NavGroup[] {
  const core: NavGroup[] = [
    {
      title: "",
      icon: <CircleHelp className="w-4 h-4" />,
      groupKey: "engineer-help",
      items: [
        {
          label: "Hilfe",
          path: "/help",
          icon: <CircleHelp className="w-4 h-4" />,
        },
      ],
    },
  ];
  const sources: NavGroup[] = [
    ...(ROLE_NAV_GROUPS.teacher || []),
    ...(ROLE_NAV_GROUPS.hr || []),
    ...(ROLE_NAV_GROUPS.admin || []),
    ...(ROLE_NAV_GROUPS.superadmin || []),
  ];
  const extraFlat: NavItem[] = [
    ...(ROLE_NAV_FLAT.teacher || []),
    ...(ROLE_NAV_FLAT.director || []),
  ];
  const extraGroup: NavGroup = {
    title: "Module",
    icon: <Wrench className="w-4 h-4" />,
    groupKey: "engineer-modules",
    items: extraFlat.filter(
      (item) =>
        engineerVisiblePath(item.path, scopes) &&
        item.path !== "/messages" &&
        item.path !== "/help"
    ),
  };
  const filtered = sources
    .map((g) => ({
      ...g,
      groupKey: `eng-${g.groupKey}`,
      items: g.items.filter((item) => engineerVisiblePath(item.path, scopes)),
    }))
    .filter((g) => g.items.length > 0);
  const groups = [...core, extraGroup, ...filtered].filter(
    (g) => g.items.length > 0
  );
  const seen = new Set<string>();
  return groups
    .map((g) => ({
      ...g,
      items: g.items.filter((item) => {
        if (seen.has(item.path)) return false;
        seen.add(item.path);
        return true;
      }),
    }))
    .filter((g) => g.items.length > 0);
}

export function Sidebar() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const role = user?.role || "";
  const engineerScopes = user?.engineer_scopes ?? [];
  const acting = Boolean(
    user?.acting_role ||
    user?.acting_teacher_id ||
    user?.acting_user_id ||
    user?.acting_partner_company_id
  );
  const navRole = (user?.acting_role || role) as typeof role;
  const engineerGroups =
    navRole === "engineer" ? buildEngineerGroups(engineerScopes) : null;
  const flatItems = engineerGroups ? null : ROLE_NAV_FLAT[navRole];
  const groups = engineerGroups || ROLE_NAV_GROUPS[navRole] || [];

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["teacher-chats-unread-count"],
    queryFn: async () => (await teacherChatApi.getUnreadCount()).count,
    refetchInterval: UNREAD_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: UNREAD_POLL_MS / 2,
    enabled: navRole === "teacher",
  });

  useEffect(() => {
    if (navRole !== "teacher") return;
    return onCascadeStateChanged(() => {
      queryClient.invalidateQueries({
        queryKey: ["teacher-chats-unread-count"],
      });
    });
  }, [navRole, queryClient]);

  const { data: dmUnreadCount = 0 } = useQuery({
    queryKey: ["direct-messages-unread-count"],
    queryFn: async () => (await directMessageApi.getUnreadCount()).count,
    refetchInterval: UNREAD_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: UNREAD_POLL_MS / 2,
    enabled: !!user && navRole !== "partner",
  });

  const { data: helpUnreadCount = 0 } = useQuery({
    queryKey: ["help-unread-count"],
    queryFn: async () => (await helpApi.getUnreadCount()).count,
    refetchInterval: UNREAD_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: UNREAD_POLL_MS / 2,
    enabled: !!user,
  });

  const { data: systemBranding } = useQuery({
    queryKey: SYSTEM_BRANDING_PUBLIC_KEY,
    queryFn: getPublicSystemBranding,
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: !!user,
  });
  const poweredByLogo =
    systemBranding?.logo_svg || systemBranding?.logo_png || null;
  const poweredByUrl = poweredByHref(systemBranding?.url);

  // Fallakte v3 (B8): Badge «Alle Fälle (n)» für Direktoren — Muster wie Teacher-Chats
  const { data: directorPendingCount = 0 } = useQuery({
    queryKey: ["director-pending-count"],
    queryFn: async () => (await directorApi.getPending()).length,
    refetchInterval: 60000,
    enabled: navRole === "director",
  });

  // HR-v2 (11.3): aktive HR-Seiten je Scope filtern
  const { data: hrScopes } = useQuery({
    queryKey: ["hr-my-scopes"],
    queryFn: () => hrApi.getMyScopes(),
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: navRole === "hr",
  });
  const showBilling = schoolBillingVisible(user);
  let visibleGroups = groups;
  if (navRole === "hr") {
    if (!hrScopes || hrScopes.module_enabled !== true) {
      visibleGroups = groups
        .map((g) => ({
          ...g,
          items: g.items.filter((item) => item.path === "/help"),
        }))
        .filter((g) => g.items.length > 0);
    } else {
      visibleGroups = groups
        .map((g) => ({
          ...g,
          items: g.items.filter(
            (item) => !item.scopePage || hrScopes.pages[item.scopePage] === true
          ),
        }))
        .filter((g) => g.items.length > 0);
    }
  }
  const { data: exportOptions } = useQuery({
    queryKey: ["timetable-export-options", navRole],
    queryFn: () => timetableExportApi.options(),
    staleTime: 60 * 1000,
    retry: false,
    enabled:
      navRole === "teacher" ||
      navRole === "director" ||
      navRole === "admin" ||
      navRole === "hr",
  });
  const showPlanExport =
    !exportOptions ||
    exportOptions.can_export ||
    exportOptions.can_edit_settings;
  if (!showPlanExport) {
    visibleGroups = visibleGroups
      .filter((g) => !(navRole === "admin" && g.groupKey === "stundenplan"))
      .map((g) => ({
        ...g,
        items: g.items.filter((item) => item.path !== "/timetable-export"),
      }))
      .filter((g) => g.items.length > 0);
  }
  if (!showBilling) {
    visibleGroups = visibleGroups
      .map((g) => ({
        ...g,
        items: g.items.filter((item) => item.path !== "/zahlung"),
      }))
      .filter((g) => g.items.length > 0);
  }
  if (role === "hr" && showBilling) {
    const arbeitenIdx = visibleGroups.findIndex(
      (g) => g.groupKey === "arbeiten"
    );
    const paymentGroup = {
      title: "Zahlung",
      icon: <CircleDollarSign className="w-4 h-4" />,
      groupKey: "zahlung",
      items: [
        {
          label: "Zahlung",
          path: "/zahlung",
          icon: <CircleDollarSign className="w-4 h-4" />,
        },
      ],
    };
    if (arbeitenIdx >= 0) {
      visibleGroups = [
        ...visibleGroups.slice(0, arbeitenIdx + 1),
        paymentGroup,
        ...visibleGroups.slice(arbeitenIdx + 1),
      ];
    } else {
      visibleGroups = [...visibleGroups, paymentGroup];
    }
  }

  const isNavActive = (path: string) => {
    return path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path);
  };

  const badgeFor = (path: string, fallback?: number) => {
    if (path === "/teacher/chats") return unreadCount as number;
    if (path === "/messages") return dmUnreadCount as number;
    if (path === "/help") return helpUnreadCount as number;
    if (path === "/director/decisions") return directorPendingCount as number;
    return fallback;
  };

  const renderNavLink = (item: NavItem, extraClass = "") => {
    const active = isNavActive(item.path);
    const badge = badgeFor(item.path, item.badge);
    const isChatAlert =
      (item.path === "/teacher/chats" ||
        item.path === "/messages" ||
        item.path === "/help") &&
      !!badge &&
      badge > 0;
    if (item.disabled) {
      return (
        <div
          key={item.path}
          title={item.disabledHint || "folgt"}
          className={`relative flex items-center gap-3 px-4 py-2 text-sm text-slate-600 cursor-not-allowed ${extraClass}`}
        >
          {item.icon}
          <span className="flex-1">{item.label}</span>
          <span className="text-[10px] uppercase tracking-wide">
            {item.disabledHint || "folgt"}
          </span>
        </div>
      );
    }
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`relative flex items-center gap-3 px-4 py-2 text-sm hover:bg-slate-800 hover:text-white transition ${isChatAlert ? "sidebar-chat-alert" : ""} ${active ? "bg-slate-800 text-white font-medium" : "text-slate-400"} ${extraClass}`}
      >
        {isChatAlert && (
          <span className="sidebar-chat-alert-rail" aria-hidden="true" />
        )}
        {item.icon}
        <span className="flex-1">{item.label}</span>
        {item.disabledHint && (
          <span className="text-[10px] uppercase tracking-wide text-slate-500">
            {item.disabledHint}
          </span>
        )}
        {!!badge && badge > 0 && (
          <span
            className={`text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isChatAlert ? "bg-rose-500" : "bg-blue-500"}`}
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </Link>
    );
  };

  const messagesNavItem: NavItem = {
    label: "Nachrichten",
    path: "/messages",
    icon: <Mail className="w-4 h-4" />,
  };

  const renderFlatItems = () => {
    if (!flatItems) return null;
    const source = showPlanExport
      ? flatItems
      : flatItems.filter((item) => item.path !== "/timetable-export");
    let items =
      navRole === "partner" && user?.partner_level !== "owner"
        ? source.filter((item) => item.path !== "/partner/team")
        : [...source];
    if ((navRole === "director" || navRole === "hr") && showBilling) {
      items = [
        ...items,
        {
          label: "Zahlung",
          path: "/zahlung",
          icon: <CircleDollarSign className="w-4 h-4" />,
        },
      ];
    }
    return items.map((item) => renderNavLink(item));
  };

  return (
    <nav className="w-56 h-full min-h-0 self-stretch bg-slate-900 text-slate-300 flex flex-col shrink-0 overflow-hidden">
      <div className="h-14 px-4 flex items-center border-b border-slate-700 shrink-0">
        <span className="text-white font-bold text-lg">Stellvertretung</span>
      </div>

      <div className="flex-1 py-2 overflow-y-auto min-h-0">
        {flatItems ? (
          renderFlatItems()
        ) : (
          <>
            {renderNavLink(messagesNavItem)}
            {visibleGroups.map((group) => {
              if (group.items.length === 1) {
                const item = group.items[0]!;
                return renderNavLink({
                  ...item,
                  icon: item.icon ?? group.icon,
                });
              }
              return (
                <SidebarGroup
                  key={group.groupKey}
                  title={group.title}
                  icon={group.icon}
                  groupKey={group.groupKey}
                >
                  {group.items.map((item) => renderNavLink(item))}
                </SidebarGroup>
              );
            })}
          </>
        )}
      </div>

      <div className="border-t border-slate-700 shrink-0">
        {user && (
          <div className="px-4 py-2">
            {acting ? null : <RoleSelector />}
            <div
              className="text-xs text-slate-500 truncate mt-0.5"
              title={user.email}
            >
              {user.email}
            </div>
          </div>
        )}
        <div className="flex items-stretch">
          <button
            type="button"
            onClick={logout}
            className="flex-1 min-w-0 px-4 py-4 text-base font-medium hover:bg-slate-800 hover:text-white transition text-left"
          >
            Abmelden
          </button>
          {poweredByLogo && (
            <a
              href={poweredByUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-1/2 flex-col items-stretch justify-center gap-0.5 px-1.5 py-1 text-[11px] text-slate-400 hover:text-slate-300 shrink-0"
            >
              <span className="whitespace-nowrap">Powered by</span>
              <TrimmedLogo
                src={poweredByLogo}
                className="block w-full h-auto"
              />
            </a>
          )}
        </div>
      </div>
    </nav>
  );
}
